import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { ProviderImageInfo } from '@app/manage-providers/provider-api.model';
import { TargetType } from '@app/profile/profile-api.model';
import { ImageLayoutComponent } from '@app/shared/components/image-layout/image-layout.component';
import { ProfilePicComponent } from '@app/shared/components/profile-pic/profile-pic.component';
import { SharedTargetService } from '@app/target/services/shared-target.service';
import {
  ResourceResult,
  TargetSuggestionType,
} from '@app/target/target-api.model';
import { IconComponent } from '@degreed/apollo';
import {
  ContentUtilsService,
  WebEnvironmentService,
} from '@degreed/lxp/services';
import { resourceTypeApolloIcon } from '@degreed/lxp/ui';
import { TranslateService } from '@ngx-translate/core';

interface ResourceResultListItem extends ResourceResult {
  displayType?: string;
  providerImageUrl?: string;
  providerImageInfo?: ProviderImageInfo;
}

@Component({
  selector: 'dgx-target-resource-list-item',
  templateUrl: './target-resource-list-item.component.html',
  styleUrls: ['./target-resource-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ProfilePicComponent, ImageLayoutComponent, IconComponent],
})
export class TargetResourceListItemComponent {
  public item = input.required<ResourceResultListItem>();
  protected readonly resourceTypeIcon = resourceTypeApolloIcon;

  private translateService = inject(TranslateService);
  private sharedTargetService = inject(SharedTargetService);
  private webEnvironmentService = inject(WebEnvironmentService);
  private contentUtils = inject(ContentUtilsService);

  public processedItem = computed(() => {
    const item = this.item();
    if (!item) {
      throw new Error('Missing resource result item');
    }

    if (item.referenceType === 'Target') {
      item.displayType = this.sharedTargetService.getTypeDisplayName(
        item.referenceSubType as TargetType | TargetSuggestionType
      );
    }

    if (item.referenceType === 'Provider') {
      const image = item.image
        ? item.image
        : '/content/img/app/no-logo-seal.png';

      item.providerImageUrl = this.webEnvironmentService.getBlobUrl(image);
    }

    return item;
  });

  public i18n = computed(() => {
    const inputType = this.contentUtils
      .getDisplayType(this.processedItem().referenceType)
      .toLowerCase();

    return this.translateService.instant(
      ['Core_Endorsed', 'OrgInternalContent_RestrictedContentNote'],
      // for OrgInternalContent_RestrictedContentNote only
      { inputType }
    );
  });
}
